// A save that must never overlap itself, and must never drop the last edit.
//
// The custom-stat editor (statisticField.vue) POSTs a brand-new row and only
// learns its id in the response callback — everything before that response is
// still, as far as the client knows, an unsaved row. So two saves that overlap
// both POST, and the deliverable ends up with two identical manual statistics.
// That is not hypothetical: a single editing session left duplicate manual rows
// on 9 of one report's 57 deliverables, each pair written in the same second.
//
// Overlapping saves are the normal case, not an edge case: the label combo box
// saves on blur and the value input saves on blur too (Enter blurs it), so
// filling in a label and then typing a value fires save twice in quick
// succession. A plain "ignore while busy" guard would stop the duplicate and
// silently throw away whatever the user typed second — usually the value they
// actually came to enter. So the second call is queued instead, and replayed
// once the in-flight request completes, by which time the row has an id and the
// replay is an update of the row we just created rather than a second insert.
//
// Extracted from the component (same reasoning as lib/refreshStatsState.mjs) so
// the ordering can be driven deterministically in test/javascript without a
// browser.
export function createSingleFlightSave(perform) {
  let inFlight = false;
  let queued = false;

  function release() {
    inFlight = false;
    if (queued) {
      queued = false;
      run();
    }
  }

  // Returns true when the call started a request, false when it was queued
  // behind one already in flight. Any number of calls made during one request
  // coalesce into a single follow-up — the replay always sends current state,
  // so replaying each of them would just repeat the same write.
  function run() {
    if (inFlight) {
      queued = true;
      return false;
    }

    inFlight = true;
    try {
      // `perform` owns the request and MUST call the callback it is handed
      // exactly once, on failure as well as success — a request that fails
      // without releasing the guard would wedge the field shut for good.
      perform(release);
    } catch (error) {
      inFlight = false;
      queued = false;
      throw error;
    }
    return true;
  }

  return {
    run,
    get inFlight() {
      return inFlight;
    },
    get queued() {
      return queued;
    },
  };
}
